import { wEmitter, EventName } from '@utils/global/event-emitter';
const SCREENSHOT_QUERY_KEY = '_screenshot';
/** 页面侧上限，必须小于服务端预算，否则服务端只能耗到硬超时 */
const READY_TIMEOUT = 8 * 1000;
/** 样式表兜底等待上限，超过就不再等，让诊断字段把问题暴露出去 */
const SHEET_TIMEOUT = 5 * 1000;
export const isScreenshotMode = () => new URLSearchParams(window.location.search).get(SCREENSHOT_QUERY_KEY) === '1';
/** link.sheet 为 null 说明样式表还没解析完或加载失败，此时页面布局是错的 */
const countPendingSheets = () => Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .filter(link => !link.sheet).length;
const countEmptyStyles = () => Array.from(document.querySelectorAll('style'))
    .filter(el => !(el.textContent || '').trim()).length;
const sumStyleBytes = () => Array.from(document.querySelectorAll('style'))
    .reduce((sum, el) => sum + (el.textContent || '').length, 0);
/**
 * 渲染容器缺中文字体时会静默 fallback，字宽全变导致布局位移，这是「样式偶尔跑偶」的常见来源。
 * 不能用 document.fonts.check：它对本地字体名 fallback 后照样返回 true，测不出缺失。
 * 只能拿一段英文和 monospace 比宽度，命中 fallback 时两者一致。
 */
const isFontAvailable = (family) => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx)
        return false;
    const text = 'mmmiiilllwww';
    ctx.font = '32px monospace';
    const fallbackWidth = ctx.measureText(text).width;
    ctx.font = `32px "${family}", monospace`;
    return ctx.measureText(text).width !== fallbackWidth;
};
/**
 * 出图有问题时靠这些字段定位，不必再去猜。
 * 页面知道自己应该长什么样，比对着像素做图像分析准确得多。
 */
const collectDiagnostics = () => ({
    pendingSheets: countPendingSheets(),
    emptyStyles: countEmptyStyles(),
    styleBytes: sumStyleBytes(),
    fontsOk: isFontAvailable('PingFang SC') || isFontAvailable('Microsoft YaHei'),
    content: { w: document.documentElement.scrollWidth, h: document.documentElement.scrollHeight },
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
    spinners: document.querySelectorAll('.loading-icon, .ant-spin, .t-loading').length,
    failedImages: Array.from(document.images).filter(img => !img.complete || img.naturalWidth === 0).length,
});
/** 双帧：第一帧回调时本帧尚未绘制，第二帧才能确认已完成一次绘制与合成 */
const nextPaint = () => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
});
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
/** link.sheet 由浏览器解析赋值，只能轮询 */
const waitForSheets = async () => {
    const deadline = Date.now() + SHEET_TIMEOUT;
    while (countPendingSheets() > 0 && Date.now() < deadline) {
        await sleep(50);
    }
};
let reported = false;
const report = async (state) => {
    if (reported)
        return;
    reported = true;
    try {
        if (state === 'ready') {
            await waitForSheets();
            await document.fonts.ready;
            // complete 只代表下载完，decode 才保证能立即绘制
            await Promise.all(Array.from(document.images).map(img => img.decode().catch(() => { })));
        }
        await nextPaint();
    }
    catch (error) {
        // 等资源自己炸了也必须把信号发出去，否则服务端只能白等到超时
        console.error('[screenshot-ready] wait resources failed', error);
    }
    window.__PREVIEW_DIAGNOSTICS__ = collectDiagnostics();
    window.__PREVIEW_STATE__ = state;
};
/**
 * 冻结动画与过渡，否则截到的可能是淡入到一半的中间帧。
 * 用 0.01ms 而不是 none：动画事件仍会照常触发，依赖 transitionend 的组件不会卡住。
 */
const freezeAnimations = () => {
    const style = document.createElement('style');
    style.textContent = `*, *::before, *::after {
    animation-duration: .01ms !important;
    animation-delay: 0s !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
    transition-delay: 0s !important;
  }`;
    document.head.appendChild(style);
};
/**
 * AI 页不走 uc-renderer，没有数据源就绪事件，由 Gogo host 在用户代码执行完（statusChanged: executed）时调用。
 * 此时 CSS 已经通过 cssInjector 写进 <style>，比盲等 READY_TIMEOUT 准得多。
 * 组件挂载和首屏数据还在后头，由截图服务在收到信号后软等一次网络静默兜住。
 */
export const reportScreenshotReady = () => {
    if (!isScreenshotMode())
        return;
    void report('ready');
};
export const setupScreenshotReady = () => {
    if (!isScreenshotMode())
        return;
    freezeAnimations();
    wEmitter.on(EventName.PAGE_DATASOURCE_READY, () => void report('ready'));
    setTimeout(() => void report('timeout'), READY_TIMEOUT);
};
